import { useEffect, useState } from "react";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Code from "@mui/icons-material/Code";
import ListAlt from "@mui/icons-material/ListAlt";
import PersonOutline from "@mui/icons-material/PersonOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { useTranslation } from "../../../hooks/useTranslation";
import { API_URL } from "../../../providers/dataProvider";
import { getRuntimeEnv } from "../../../providers/getRuntimeEnv";
import { isFailed, startRequestLog, useRequestLog } from "./requestLog";
import { RequestLogDrawer } from "./RequestLogDrawer";
import { SessionDialog } from "./SessionDialog";

const SP_API = "https://developer-docs.amazon.com/sp-api";

// Every address here was checked to open (the SP-API pages by their title,
// as that site answers missing pages too).
const GROUPS: { key: string; links: { key: string; url: string }[] }[] = [
  {
    key: "syncrosale",
    links: [
      { key: "swagger", url: `${API_URL}/swagger-ui/index.html` },
      { key: "openApi", url: `${API_URL}/v3/api-docs` },
    ],
  },
  {
    key: "spApi",
    links: [
      { key: "spApiHome", url: `${SP_API}/` },
      {
        key: "listingsItems",
        url: `${SP_API}/docs/listings-items-api-v2021-08-01-reference`,
      },
      { key: "feeds", url: `${SP_API}/docs/feeds-api-v2021-06-30-reference` },
      {
        key: "listingsFeedTypes",
        url: `${SP_API}/docs/listings-feed-type-values`,
      },
      {
        key: "listingsGuide",
        url: `${SP_API}/docs/building-listings-management-workflows-guide`,
      },
      {
        key: "notifications",
        url: `${SP_API}/docs/notifications-api-v1-reference`,
      },
      {
        key: "productPricing",
        url: `${SP_API}/docs/product-pricing-api-v0-reference`,
      },
      {
        key: "catalogItems",
        url: `${SP_API}/docs/catalog-items-api-v2022-04-01-reference`,
      },
      { key: "orders", url: `${SP_API}/docs/orders-api-v0-reference` },
      {
        key: "productTypes",
        url: `${SP_API}/docs/product-type-definitions-api-v2020-09-01-reference`,
      },
      { key: "endpoints", url: `${SP_API}/docs/sp-api-endpoints` },
      { key: "rateLimits", url: `${SP_API}/docs/usage-plans-and-rate-limits` },
    ],
  },
];

// The developers' menu in the admin header, on the test environment only
// (on the live one it is not there at all): two tools, "My session" and the
// recent API requests, then the links (API docs, repositories, Amazon's
// SP-API docs). A red dot on the button tells a request failed.
export const DevLinks = () => {
  const { translate } = useTranslation("pages.admin.header.devLinks");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const dev = getRuntimeEnv() === "dev";
  const requests = useRequestLog();
  const failed = requests.filter(isFailed).length;

  // the log listens from the first admin page on, on the test environment only
  useEffect(() => {
    if (dev) startRequestLog();
  }, [dev]);

  if (!dev) return null;

  const tools = [
    {
      key: "session",
      Icon: PersonOutline,
      label: translate("tools.session"),
      onClick: () => setSessionOpen(true),
    },
    {
      key: "requests",
      Icon: ListAlt,
      label: translate("tools.requests", {
        count: requests.length,
        failed,
      }),
      onClick: () => setLogOpen(true),
    },
  ];

  return (
    <>
      <Tooltip title={translate("hint")}>
        <Button
          size="small"
          color="inherit"
          startIcon={
            <Badge
              variant="dot"
              color="error"
              invisible={failed === 0}
              overlap="circular"
            >
              <Code sx={{ fontSize: "16px !important" }} />
            </Badge>
          }
          endIcon={<ExpandMore sx={{ fontSize: "16px !important" }} />}
          aria-haspopup="menu"
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: 13,
            fontWeight: 400,
            minWidth: 0,
            height: 26,
            px: 0.75,
            "& .MuiButton-startIcon": { mr: 0.75 },
            "& .MuiButton-endIcon": { ml: 0.25 },
          }}
        >
          {translate("button")}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 300, maxHeight: 520 } }}
      >
        <ListSubheader
          sx={{ lineHeight: "32px", fontSize: 11, letterSpacing: 0.6 }}
        >
          {translate("groups.tools").toUpperCase()}
        </ListSubheader>
        {tools.map(({ key, Icon, label, onClick }) => (
          <MenuItem
            key={key}
            dense
            onClick={() => {
              setAnchor(null);
              onClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Icon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
        {GROUPS.flatMap((group) => [
          <ListSubheader
            key={group.key}
            sx={{ lineHeight: "32px", fontSize: 11, letterSpacing: 0.6 }}
          >
            {translate(`groups.${group.key}`).toUpperCase()}
          </ListSubheader>,
          ...group.links.map((link) => (
            <MenuItem
              key={link.key}
              component="a"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchor(null)}
              dense
            >
              <ListItemText primary={translate(`links.${link.key}`)} />
              <ListItemIcon sx={{ minWidth: 0, ml: 2 }}>
                <OpenInNew sx={{ fontSize: 14 }} />
              </ListItemIcon>
            </MenuItem>
          )),
        ])}
      </Menu>
      <SessionDialog open={sessionOpen} onClose={() => setSessionOpen(false)} />
      <RequestLogDrawer open={logOpen} onClose={() => setLogOpen(false)} />
    </>
  );
};
